import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Calendar, CheckCircle2, Link2, X, Video } from "lucide-react";
import { api } from "../../lib/api";

export default function GoogleCalendarConnect() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/admin/calendar/status"); setStatus(data); }
    catch { setStatus({ configured: false, connected: false }); }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const err = p.get("calendar_error");
    if (p.get("calendar_connected") === "1") { toast.success("Google Calendar connected — bookings now create Meet links."); load(); }
    else if (err) {
      const friendly = {
        access_denied: "Google blocked access. If the OAuth app is in Testing mode, add your Google account as a Test User in Google Cloud Console → OAuth consent screen, then retry.",
        state_expired: "The connection link expired. Please click Connect again.",
        invalid_client: "Google rejected the client secret. Double-check GOOGLE_CLIENT_SECRET.",
      }[err] || `Google Calendar connection failed: ${err}`;
      toast.error(friendly, { duration: 12000 });
    }
    if (p.get("calendar_connected") || p.get("calendar_error")) {
      p.delete("calendar_connected"); p.delete("calendar_error");
      const qs = p.toString();
      window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : ""));
    }
  }, []);

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/calendar/connect");
      window.location.href = data.authorization_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start Google connection.");
      setBusy(false);
    }
  };

  const disconnect = async () => {
    setBusy(true);
    try { await api.post("/admin/calendar/disconnect"); toast.message("Google Calendar disconnected."); load(); }
    catch { toast.error("Could not disconnect."); }
    finally { setBusy(false); }
  };

  if (!status) return null;
  const connected = status.connected;

  return (
    <div data-testid="gcal-connect" className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className={`flex h-9 w-9 items-center justify-center rounded-full ${connected ? "bg-[#4b7a3a]/12" : "bg-[#1e3d3a]/8"}`}>
            {connected ? <CheckCircle2 className="h-4.5 w-4.5 text-[#4b7a3a]" /> : <Calendar className="h-4.5 w-4.5 text-[#1e3d3a]/60" />}
          </div>
          <div>
            <p className="text-sm font-medium text-[#1e3d3a] flex items-center gap-1.5">
              <Video className="h-3.5 w-3.5 text-[#a3823f]" /> Google Calendar &amp; Meet
            </p>
            {connected ? (
              <p className="text-[11px] text-[#1e3d3a]/55" data-testid="gcal-status-connected">
                Connected as <span className="text-[#4b7a3a]">{status.email}</span> · bookings auto-create Meet links
              </p>
            ) : status.configured ? (
              <p className="text-[11px] text-[#1e3d3a]/55" data-testid="gcal-status-disconnected">
                Connect your Google account so bookings land on your calendar with a Meet link.
              </p>
            ) : (
              <p className="text-[11px] text-[#a3823f]" data-testid="gcal-status-unconfigured">
                Needs GOOGLE_CLIENT_ID &amp; GOOGLE_CLIENT_SECRET in the backend before it can connect.
              </p>
            )}
          </div>
        </div>
        {connected ? (
          <button type="button" data-testid="gcal-disconnect" onClick={disconnect} disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-full border border-[#1e3d3a]/20 px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] text-[#1e3d3a] hover:border-[#b23a48] hover:text-[#b23a48] disabled:opacity-50 transition-colors duration-300">
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <X className="h-3 w-3" />} Disconnect
          </button>
        ) : (
          <button type="button" data-testid="gcal-connect-btn" onClick={connect} disabled={busy || !status.configured}
            className="inline-flex items-center gap-1.5 rounded-full bg-[#1e3d3a] text-white px-4 py-1.5 text-[10px] uppercase tracking-[0.16em] font-semibold hover:bg-[#c9a961] hover:text-[#1e3d3a] disabled:opacity-40 disabled:hover:bg-[#1e3d3a] disabled:hover:text-white transition-colors duration-300">
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Link2 className="h-3 w-3" />} Connect Google Calendar
          </button>
        )}
      </div>
    </div>
  );
}
